import type { LucideIcon } from 'lucide-react';
import { type ReactNode, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface CardShellProps {
  title: string;
  icon: LucideIcon;
  children?: ReactNode;
  className?: string;
  bannerStyle?: boolean;
}

/**
 * Wrapper visual comum dos cards do dashboard.
 * Usa CSS variables de tema (--card-bg, --card-border, --blur).
 * forwardRef para permitir composicao futura.
 */
export const CardShell = forwardRef<
  HTMLDivElement,
  CardShellProps & React.HTMLAttributes<HTMLDivElement>
>(({ title, icon: Icon, children, className, bannerStyle, ...rest }, ref) => (
  <div
    ref={ref}
    {...rest}
    className={cn(
      'flex h-full flex-col overflow-hidden rounded-xl border shadow-[var(--shadow-card)] backdrop-blur-[var(--blur)]',
      'border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--fg)]',
      className,
    )}
  >
    <div
      className={cn(
        'flex items-center gap-2 px-4',
        bannerStyle ? 'h-full' : 'h-12 border-b border-[var(--border)]',
      )}
    >
      <Icon className="h-4 w-4 text-[var(--fg-muted)]" />
      <h3 className="text-sm font-medium tracking-tight">{title}</h3>
    </div>
    {!bannerStyle && (
      <div className="flex flex-1 items-center justify-center p-4">
        {children}
      </div>
    )}
  </div>
));
CardShell.displayName = 'CardShell';

interface EmptyStateProps {
  icon: LucideIcon;
  message: string;
  cta?: { label: string; href: string };
}

export function EmptyState({ icon: Icon, message, cta }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <Icon
        className="h-8 w-8 text-[var(--fg-muted)] opacity-40"
        strokeWidth={1.5}
      />
      <p className="max-w-xs text-xs text-[var(--fg-muted)]">{message}</p>
      {cta && (
        <Link
          to={cta.href}
          className={cn(
            'inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition',
            'bg-[var(--accent)] text-[var(--accent-fg)] hover:opacity-90',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--card-bg)]',
          )}
        >
          {cta.label}
        </Link>
      )}
    </div>
  );
}
